import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { trpc } from '@/config/trpc'
import { useNavigate } from 'react-router'
import { PricingCard } from '@/components/pricing/pricing-card'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/shadcn/toggle-group'
import {
  freePlan,
  standardPlan,
  proPlan,
  enterprisePlan,
  purchasablePlanTypes,
  type PricingPlan,
  type PurchasablePlan,
  type BillingInterval,
} from '@backend/lib/config/pricing'

type ExtendedPricingPlan = PricingPlan & {
  cta: string
  disabled: boolean
  onClick: () => void
  selected: boolean
}

export function PricingPage() {
  const navigate = useNavigate()
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('month')

  const getInitialPlans = (): ExtendedPricingPlan[] => [
    {
      ...freePlan,
      cta: 'Get Started',
      disabled: true,
      onClick: () => navigate('/account/subscription'),
      selected: false,
    },
    {
      ...standardPlan,
      cta: 'Upgrade Now',
      disabled: true,
      onClick: () => {},
      selected: false,
    },
    {
      ...proPlan,
      cta: 'Upgrade Now',
      disabled: true,
      onClick: () => {},
      selected: false,
    },
    {
      ...enterprisePlan,
      cta: 'Contact Us',
      disabled: false,
      onClick: () => window.open('https://getcassi.ai/contact', '_blank', 'noopener,noreferrer'),
      selected: false,
    },
  ]

  const [plans, setPlans] = useState<ExtendedPricingPlan[]>(getInitialPlans())

  const { data: isUserSubscribed } = trpc.stripe.isUserSubscribed.useQuery()
  const { data: userSubscription } = trpc.userSubscription.get.useQuery()

  const isTrial = userSubscription?.isSubscribed && userSubscription.isTrial
  // AppSumo lifetime plans outrank Stripe — buying Standard would charge for
  // nothing (the backend refuses the checkout too). Tier changes live on AppSumo.
  const isAppsumo = userSubscription?.isSubscribed && userSubscription.source === 'appsumo'

  // The purchased tier, for marking the matching card. Stripe's mirror knows
  // the planId; the entitlement row (userSubscription) covers manually
  // granted tiers too.
  const currentTier =
    userSubscription?.isSubscribed && !userSubscription.isTrial ? userSubscription.tier : undefined

  useEffect(() => {
    setPlans((prevPlans) =>
      prevPlans.map((plan) => {
        if ((purchasablePlanTypes as readonly string[]).includes(plan.id)) {
          const isPaidSubscriber = isUserSubscribed?.isSubscribed === true && !isTrial
          const isCurrentPlan = isPaidSubscriber && currentTier === plan.id
          if (isAppsumo) {
            return {
              ...plan,
              cta: 'Manage on AppSumo',
              disabled: false,
              onClick: () =>
                window.open('https://appsumo.com/account/products/', '_blank', 'noopener'),
              selected: false,
            }
          }
          // A paid subscriber switches the existing subscription in place
          // (checkout refuses while one is active); everyone else goes
          // through checkout.
          const isSwitch = isPaidSubscriber && !isCurrentPlan
          return {
            ...plan,
            cta: isCurrentPlan
              ? 'Current Plan'
              : isSwitch
                ? `Switch to ${plan.name}`
                : isTrial
                  ? 'Subscribe'
                  : 'Upgrade Now',
            disabled: isCurrentPlan,
            onClick: isSwitch
              ? () => handleSwitchPlan(plan as PurchasablePlan)
              : () => handleSubscribe(plan as PurchasablePlan),
            selected: isCurrentPlan,
          }
        }

        if (plan.name === 'Free') {
          return {
            ...plan,
            selected: isUserSubscribed?.isSubscribed === false && !isTrial && !isAppsumo,
          }
        }

        return plan
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserSubscribed, billingInterval, isTrial, isAppsumo, currentTier])

  const utils = trpc.useUtils()
  const { mutateAsync: createCheckoutSession } = trpc.stripe.createCheckoutSession.useMutation()
  const { mutateAsync: changePlan, isPending: isSwitching } = trpc.stripe.changePlan.useMutation()

  const handleSubscribe = async (plan: PurchasablePlan) => {
    if (isAppsumo) return
    if (isUserSubscribed?.isSubscribed && !isTrial) return

    try {
      const result = await createCheckoutSession({ planId: plan.id, billingInterval })
      if (result.url === null) throw new Error('no url received')
      window.location.href = result.url
    } catch (error) {
      console.error('Error initiating checkout:', error)
    }
  }

  const handleSwitchPlan = async (plan: PurchasablePlan) => {
    if (isAppsumo || isSwitching) return
    if (
      !confirm(
        `Switch to the ${plan.name} plan? The price difference is prorated and invoiced right away, and your new limits apply immediately.`,
      )
    ) {
      return
    }

    try {
      await changePlan({ planId: plan.id })
      await Promise.all([
        utils.stripe.isUserSubscribed.invalidate(),
        utils.userSubscription.get.invalidate(),
        utils.usageLimit.get.invalidate(),
      ])
      toast.success(`You are now on the ${plan.name} plan`)
    } catch (error) {
      toast.error('Could not switch plans', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      })
    }
  }

  return (
    <section className="mx-auto mt-[3vh] max-w-7xl flex-1 p-6">
      <div className="mb-8 text-center">
        <h2 className="text-foreground mb-4 text-4xl font-bold">Simple, Transparent Pricing</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl text-xl">
          Choose the plan that works best for you and start creating engaging social media content
          today.
        </p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <ToggleGroup
            type="single"
            value={billingInterval}
            onValueChange={(value) => {
              if (value) setBillingInterval(value as BillingInterval)
            }}
            className="bg-muted rounded-lg p-1">
            <ToggleGroupItem
              value="month"
              className="data-[state=on]:bg-background data-[state=on]:shadow-xs rounded-md px-4 py-2 text-sm">
              Monthly
            </ToggleGroupItem>
            <ToggleGroupItem
              value="year"
              className="data-[state=on]:bg-background data-[state=on]:shadow-xs rounded-md px-4 py-2 text-sm">
              Annual
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {plans.length > 0 && (
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2 xl:grid-cols-4">
          {plans.map((plan) => (
            <PricingCard
              key={plan.id}
              title={plan.name}
              price={plan.amount}
              annualPrice={'annualAmount' in plan ? plan.annualAmount : undefined}
              billingInterval={billingInterval}
              currency={plan.currency}
              features={plan.features.filter((f) => f.name !== undefined).map((f) => f.name!)}
              cta={plan.cta}
              disabled={plan.disabled}
              onClick={plan.onClick}
              selected={plan.selected}
            />
          ))}
        </div>
      )}
    </section>
  )
}
